import { useMemo, useState, type ReactNode } from 'react';
import { OFF } from '../../lib/off';
import { Link, useParams } from 'react-router';
import { ALL_FEATURE_SETS, MAILBOX_POLICIES, formatPhoneNumber, isInternalRoute } from '@stitch/pbx-adapter/catalog';
import { referencesTo } from '@stitch/pbx-adapter/describe';
import { phoneModel } from '@stitch/pbx-adapter/phones';
import type { FieldValues } from '@stitch/pbx-adapter/catalog';
import { ApiError, api, type Account, type Directory, type EmergencyLocation, type Intent, type InviteResult } from '../../api/client';
import { DestinationPicker } from '../../components/pickers';
import { ReviewDrawer, type ReviewItem } from '../../components/ReviewDrawer';
import { useTenant } from '../../components/Shell';
import { TranscriptSwitch } from '../../components/TranscriptSwitch';
import { Button, Card, Chip, Dialog, Empty, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { dateOnly, plural } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';
import { FeatureSetChip, fullName, pbxZoneLabel, pbxZoneNames } from './People';
import { ShareLink } from './Team';
import { usePresence } from '../../components/Presence';
import { PresenceWithDndOff, TurnOffDnd, useCanTurnOffDnd } from '../../components/DndOff';
import { TheirPhoto } from '../../components/TheirPhoto';
import { SeatCard } from '../../components/SeatCard';
import { SharedPhoneCard } from '../../components/SharedPhoneCard';
import { VoicemailGreetingCard } from '../../components/VoicemailGreeting';
import { PersonTextsCard, useCompanyAdmin } from '../../components/TextRouting';

/** A staged change on this page: a setting of theirs, or a number pointed at them. */
type Staged = { key: string; description: string; intent: Intent };

const CALL_FIELDS = ['dnd', 'noAnswerSeconds', 'forwardNoAnswer', 'forwardBusy', 'forwardAll'] as const;
type CallField = (typeof CALL_FIELDS)[number];

const LABELS: Record<string, string> = {
  callerId: 'caller ID',
  dnd: 'do not disturb',
  noAnswerSeconds: 'ring time',
  forwardNoAnswer: 'when they don’t answer',
  forwardBusy: 'when they’re on a call',
  forwardAll: 'forward all calls',
  timeZone: 'time zone',
  emergencyLocation: 'emergency address',
  mailboxPolicy: 'old voicemail messages',
  featureSet: 'type'
};

/** Someone's own page: who they are, their numbers and caller ID, how calls reach them, their phone, and sign-in. */
export function PersonPage() {
  const { tenant } = useTenant();
  const { extension = '' } = useParams();
  const d = tenant.domain;
  const base = `/t/${encodeURIComponent(d)}`;
  const people = useData(() => api.people(d), [d]);
  const [version, setVersion] = useState(0);
  const account = useData(() => api.account(d, 'person', extension), [d, extension, version]);
  const numbers = useData(() => api.accounts(d, 'number').catch(() => null), [d, version]);
  const phones = useData(() => api.accounts(d, 'phone').catch(() => null), [d, version]);
  const directory = useData(() => api.directory(d).catch(() => null), [d]);
  const routing = useData(() => api.routing(d).catch(() => null), [d, version]);
  const access = useData(() => api.access(d).catch(() => null), [d]);
  const zones = useData(() => api.options(d, 'timezone').catch(() => ({ options: [] })), [d]);
  const locations = useData(() => api.emergencyLocations(d).catch(() => null), [d]);
  const presence = usePresence(d);
  // Numbers for texts (1.12.32): the company's own admins see whose texts come to them, and give them more.
  const admin = useCompanyAdmin(d);
  const canDndOff = useCanTurnOffDnd(d);
  const mailbox = useData(() => api.voicemail(d, extension).catch(() => null), [d, extension]);
  for (const o of zones.data?.options ?? []) if (o.value) pbxZoneNames.set(o.value, o.label);

  const [staged, setStaged] = useState<Staged[]>([]);
  const [reviewing, setReviewing] = useState(false);
  const [reviewKey, setReviewKey] = useState(0);
  const [dialog, setDialog] = useState<null | 'number' | 'callerId' | 'calls' | 'e911'>(null);
  const [invited, setInvited] = useState<InviteResult | null>(null);
  const [inviteError, setInviteError] = useState('');

  const row = people.data?.people.find((p) => p.extension === extension);
  // A shared phone (1.13.0, 3067): a place, not a person.
  const place = people.data?.sharedPhones?.find((p) => p.extension === extension) ?? null;
  const values: FieldValues = account.data?.account.values ?? {};
  const fields = people.data?.fields ?? [];
  const writable = (people.data?.writable ?? false) && tenant.writable;
  const canWrite = (f: string) => writable && (people.data?.writableFields ?? []).includes(f);
  // Only what this phone system has for them: settings it doesn't keep aren't shown at all.
  const has = (f: string) => fields.includes(f) && values[f] !== undefined;
  const dir = directory.data ?? undefined;
  const name = row ? fullName(row) : extension;

  const pending: FieldValues = {};
  for (const s of staged) if (s.intent.op === 'person.update') Object.assign(pending, s.intent.set);
  const shown = (f: string) => pending[f] ?? values[f] ?? '';

  // Real numbers only: platform trigger numbers (+1 899) aren't anyone's own number or caller ID.
  const realNumbers = (numbers.data?.accounts ?? []).filter((n) => !isInternalRoute(n.extension));
  const theirNumbers = realNumbers.filter((n) => n.values.destination === extension);
  const theirRoutes = (numbers.data?.accounts ?? []).filter((n) => isInternalRoute(n.extension) && n.values.destination === extension);
  const stagedNumbers = staged.filter((s) => s.intent.op === 'account.update' && s.intent.kind === 'number').map((s) => (s.intent as { extension: string }).extension);
  const theirPhones = (phones.data?.accounts ?? []).filter((p) => p.values.user === extension);
  const refs = useMemo(() => (routing.data ? referencesTo(extension, routing.data.accounts, dir?.mailboxPrefix ? { mailboxPrefix: dir.mailboxPrefix } : {}) : []), [routing.data, extension, dir]);
  const teamsIn = useMemo(
    () => refs.filter((r) => r.how === 'member' && (r.kind === 'ringGroup' || r.kind === 'callQueue')).map((r) => ({ extension: r.extension, name: r.name, kind: r.kind, where: r.where })),
    [refs]
  );
  const member = access.data?.members.find((m) => m.extension === extension || (row?.email && m.email === row.email.toLowerCase()));
  const invite = access.data?.invites.find((i) => i.extension === extension || (row?.email && i.email === row.email.toLowerCase()));

  const savedAddresses = (locations.data?.locations ?? []).filter((l) => l.active || l.id === values.emergencyLocation);
  const locationText = (id: string) => {
    const l = locations.data?.locations.find((x) => x.id === id);
    return l ? l.name || l.address : id ? `address ${id}` : 'none';
  };

  const stageSettings = (set: FieldValues) => {
    const changed = Object.fromEntries(Object.entries(set).filter(([k, v]) => v !== (values[k] ?? '')));
    setStaged((cur) => {
      const others = cur.filter((s) => s.key !== 'settings');
      const prev = cur.find((s) => s.key === 'settings')?.intent;
      const merged: FieldValues = { ...(prev && prev.op === 'person.update' ? (prev.set as FieldValues) : {}), ...set };
      const real: FieldValues = Object.fromEntries(Object.entries(merged).filter(([k, v]) => v !== (values[k] ?? '')));
      if (!Object.keys(real).length) return others;
      return [
        ...others,
        {
          key: 'settings',
          description: `Change ${name}: ${Object.entries(real)
            .map(([k, v]) => `${LABELS[k] ?? k} → ${k === 'emergencyLocation' ? locationText(v) : describe(k, v, dir)}`)
            .join(', ')}`,
          intent: { op: 'person.update', extension, set: real } as Intent
        }
      ];
    });
    return Object.keys(changed).length > 0;
  };

  const stageNumber = (n: Account) => {
    setStaged((cur) => [
      ...cur.filter((s) => s.key !== `number-${n.extension}`),
      {
        key: `number-${n.extension}`,
        description: `Send calls to ${formatPhoneNumber(n.extension)} to ${name}${n.values.destination ? ` (now ${nameIn(dir, n.values.destination)})` : ''}`,
        intent: { op: 'account.update', kind: 'number', extension: n.extension, set: { destination: extension } } as Intent
      }
    ]);
  };

  /** A messaging-only person (found by email) gets this extension: they see their own phone from then on. */
  async function linkThem(memberId: string) {
    setInviteError('');
    try {
      await api.changeMember(d, memberId, { extension });
      access.reload();
    } catch (err) {
      setInviteError(err instanceof ApiError || err instanceof Error ? err.message : 'That didn’t work.');
    }
  }

  async function inviteThem() {
    if (!row?.email) return;
    setInviteError('');
    try {
      setInvited(await api.invite(d, { email: row.email, name, role: 'user', extension }));
      access.reload();
    } catch (err) {
      setInviteError(err instanceof ApiError || err instanceof Error ? err.message : 'That didn’t send.');
    }
  }

  if (people.error || account.error) {
    const missing = account.error instanceof ApiError && account.error.status === 404;
    return (
      <>
        <PageHeader eyebrow="People" title={missing ? 'Not here' : name} />
        <div className="content">
          <Card>
            {missing ? (
              <Empty title={`There’s no one at extension ${extension}`}>
                <Link to={`${base}/people`}>Back to People</Link>
              </Empty>
            ) : (
              <LoadError error={(people.error ?? account.error)!} retry={() => (people.reload(), account.reload())} />
            )}
          </Card>
        </div>
      </>
    );
  }
  if (!people.data || !account.data) {
    return (
      <>
        <PageHeader eyebrow="People" title="Loading" />
        <div className="content">
          <Card>
            <Loading label="Loading" />
          </Card>
        </div>
      </>
    );
  }

  const callerId = shown('callerId');
  const callerText = callerId ? `${formatPhoneNumber(callerId)}${theirNumbers.some((n) => n.extension === callerId) ? ' (their number)' : ''}` : 'The company’s usual number';
  const forwardAll = shown('forwardAll');
  const dnd = shown('dnd') === 'true';
  // On now (as the phone system has it, not an edit waiting for review): admins can turn it off at once.
  const dndOff = canDndOff && canWrite('dnd') && values.dnd === 'true';

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <Link to={`${base}/people`}>People</Link> · Extension {extension}
          </>
        }
        title={name}
        chips={
          <>
            <PresenceWithDndOff
              domain={d}
              extension={extension}
              name={name}
              p={presence?.people[extension]}
              dir={dir}
              canTurnOff={canDndOff && canWrite('dnd')}
              onDone={() => setVersion((x) => x + 1)}
            />
            {fields.includes('featureSet') && <FeatureSetChip value={row?.featureSet ?? ''} />}
            {place && <Chip>Shared phone</Chip>}
            {has('dnd') && dnd && <Chip tone="warn">Do not disturb</Chip>}
            {has('forwardAll') && forwardAll && <Chip tone="warn">Forwarding to {nameIn(dir, forwardAll)}</Chip>}
          </>
        }
        actions={
          <Link className="btn secondary" to={`${base}/view/${encodeURIComponent(extension)}`}>
            <Icon name="eye" size={16} /> See what they see
          </Link>
        }
      />
      <div className="content person-page">
        {!writable && (
          <Notice tone="warn" title="View-only" icon="lock">
            {OFF.readOnly}
          </Notice>
        )}
        <div className="person-grid">
          <div className="person-main">
            <Card title="Details" action={writable ? <Link to={`${base}/people?edit=${encodeURIComponent(extension)}`}>Edit</Link> : undefined}>
              <dl className="person-facts">
                <Fact label="Name">{name}</Fact>
                <Fact label="Photo">
                  {member ? (
                    <TheirPhoto tenant={d} userId={member.id} name={name} extension={member.extension} canChange />
                  ) : (
                    <span className="muted">Once they use Stitch</span>
                  )}
                </Fact>
                <Fact label="Extension">
                  <span className="mono">{extension}</span>
                </Fact>
                <Fact label="Email">{row?.email || <span className="muted">None</span>}</Fact>
                {fields.includes('mobile') && <Fact label="Mobile">{row?.mobile ? formatPhoneNumber(row.mobile) : <span className="muted">None</span>}</Fact>}
                {has('timeZone') && <Fact label="Time zone">{shown('timeZone') ? pbxZoneLabel(shown('timeZone')) : 'The company’s'}</Fact>}
              </dl>
            </Card>

            <Card
              title="Phone numbers"
              action={
                writable && numbers.data ? (
                  <Button size="small" variant="secondary" icon="plus" onClick={() => setDialog('number')}>
                    Give them a number
                  </Button>
                ) : undefined
              }
            >
              <div className="card-body section-stack">
                {numbers.data === null ? (
                  <span className="muted">Phone numbers can’t be read from this phone system yet.</span>
                ) : theirNumbers.length + stagedNumbers.length === 0 ? (
                  <span className="muted">No number of their own: callers reach them by extension, through a menu, ring group or queue.</span>
                ) : (
                  <ul className="plain-list">
                    {theirNumbers.map((n) => (
                      <li key={n.extension}>
                        <Icon name="hash" size={16} /> <strong>{formatPhoneNumber(n.extension)}</strong>
                        {n.live?.number?.label && <span className="muted"> · {n.live.number.label}</span>}
                      </li>
                    ))}
                    {stagedNumbers.map((n) => (
                      <li key={`s-${n}`}>
                        <Icon name="hash" size={16} /> <strong>{formatPhoneNumber(n)}</strong> <Chip small tone="brand">Waiting for review</Chip>
                      </li>
                    ))}
                  </ul>
                )}
                {theirRoutes.length > 0 && (
                  <span className="muted small">
                    Also reached through {theirRoutes.length === 1 ? 'an internal route' : `${theirRoutes.length} internal routes`} Stitch uses between its phone platforms
                    ({theirRoutes.map((n) => formatPhoneNumber(n.extension)).join(', ')}).
                  </span>
                )}
                {has('callerId') && (
                  <div className="setting-row">
                    <span className="cell-stack">
                      <strong>Caller ID for their calls out</strong>
                      <span className="muted">{callerText}</span>
                    </span>
                    {canWrite('callerId') ? (
                      <Button size="small" variant="secondary" onClick={() => setDialog('callerId')}>
                        Change
                      </Button>
                    ) : (
                      <Locked />
                    )}
                  </div>
                )}
                <div className="setting-row e911">
                  <span className="cell-stack">
                    <strong>Emergency address (911)</strong>
                    {(() => {
                      const id = shown('emergencyLocation');
                      const loc = id ? locations.data?.locations.find((l) => l.id === id) : undefined;
                      const waiting = pending.emergencyLocation !== undefined;
                      if (loc)
                        return (
                          <span>
                            {loc.name || 'Saved address'}
                            <span className="muted"> · {loc.address}</span> {waiting && <Chip small tone="brand">Waiting for review</Chip>}
                          </span>
                        );
                      return (
                        <span className="muted">
                          {id ? 'An address the company no longer lists. ' : 'Not set for them yet. '}
                          It’s where emergency services are sent when they call 911 from their phone.
                        </span>
                      );
                    })()}
                  </span>
                  {!has('emergencyLocation') ? null : !canWrite('emergencyLocation') ? (
                    <Locked />
                  ) : savedAddresses.length ? (
                    <Button size="small" variant="secondary" onClick={() => setDialog('e911')}>
                      {values.emergencyLocation ? 'Change' : 'Choose'}
                    </Button>
                  ) : (
                    <Locked text={locations.data ? 'Ask Stitch to add your addresses' : 'Addresses unavailable'} />
                  )}
                </div>
              </div>
            </Card>

            {admin && extension && <PersonTextsCard domain={d} extension={extension} name={name} teams={teamsIn} base={base} />}

            {CALL_FIELDS.some((f) => has(f)) && (
              <Card
                title="When calls come in"
                action={
                  CALL_FIELDS.some((f) => canWrite(f)) ? (
                    <Button size="small" variant="secondary" onClick={() => setDialog('calls')}>
                      Change
                    </Button>
                  ) : undefined
                }
              >
                <dl className="person-facts">
                  {has('dnd') && (
                    <Fact label="Do not disturb">
                      {dnd ? 'On: calls go straight to voicemail' : 'Off'}
                      {dndOff && (
                        <>
                          {' '}
                          <TurnOffDnd
                            domain={d}
                            extension={extension}
                            name={name}
                            trigger={<span className="dnd-off-action">Turn it off</span>}
                            onDone={() => setVersion((x) => x + 1)}
                          />
                        </>
                      )}
                    </Fact>
                  )}
                  {has('noAnswerSeconds') && <Fact label="Ring them for">{shown('noAnswerSeconds') ? `${shown('noAnswerSeconds')} seconds` : 'The usual time'}</Fact>}
                  {has('forwardNoAnswer') && <Fact label="When they don’t answer">{shown('forwardNoAnswer') ? nameIn(dir, shown('forwardNoAnswer')) : 'Their voicemail'}</Fact>}
                  {has('forwardBusy') && <Fact label="When they’re on a call">{shown('forwardBusy') ? nameIn(dir, shown('forwardBusy')) : 'Call waiting, then voicemail'}</Fact>}
                  {has('forwardAll') && <Fact label="Forward all calls">{forwardAll ? nameIn(dir, forwardAll) : 'Off'}</Fact>}
                </dl>
                {!CALL_FIELDS.some((f) => canWrite(f)) && (
                  <div className="card-foot">
                    <Locked text="These wait for confirmation on your phone system. Stitch support can change them for you." />
                  </div>
                )}
              </Card>
            )}
          </div>

          <div className="person-side">
            <Card title="Desk phone" action={<Link to={`${base}/phones`}>Desk phones</Link>}>
              <div className="card-body section-stack">
                {phones.data === null ? (
                  <span className="muted">Desk phones can’t be read from this phone system yet.</span>
                ) : theirPhones.length === 0 ? (
                  <span className="muted">No desk phone. They can use the apps.</span>
                ) : (
                  <ul className="plain-list">
                    {theirPhones.map((p) => {
                      const m = phoneModel(p.values.model ?? '');
                      return (
                        <li key={p.extension}>
                          <Link to={`${base}/phones/${encodeURIComponent(p.extension)}`}>
                            <Icon name="device" size={16} /> {m ? `${m.vendor} ${m.model}` : p.values.model || 'Desk phone'}
                          </Link>
                          {p.live?.phone ? p.live.phone.online ? <Chip small tone="ok">Online</Chip> : <Chip small tone="warn">Offline</Chip> : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </Card>

            <Card
              title="Voicemail"
              action={
                <Link className="link-btn" to={`${base}/view/${encodeURIComponent(extension)}#voicemail`}>
                  Listen
                </Link>
              }
            >
              <div className="card-body section-stack">
                {mailbox.data && (
                  <strong>
                    {mailbox.data.total === 0 ? 'No messages' : `${plural(mailbox.data.total, 'message')}${mailbox.data.newCount ? `, ${mailbox.data.newCount} new` : ''}`}
                  </strong>
                )}
                <span>
                  To reach their voicemail directly, dial <strong className="mono">{(dir?.mailboxPrefix ?? '') + extension}</strong>.
                </span>
                <span className="muted">{row?.email ? `New messages are emailed to ${row.email}.` : 'Add an email address to have new messages emailed to them.'}</span>
                {mailbox.data?.transcripts && (
                  <TranscriptSwitch domain={d} mailbox={extension} own={false} whose={`${name}’s`} state={mailbox.data.transcripts} onChanged={mailbox.reload} />
                )}
                {/* What the mailbox does with old messages (1.12.33, Teampace 2977): Stitch's standard is Make room. */}
                {has('mailboxPolicy') && (
                  <div className="field">
                    <label htmlFor="mailbox-policy">Old messages</label>
                    {canWrite('mailboxPolicy') ? (
                      <select id="mailbox-policy" className="input" value={shown('mailboxPolicy')} onChange={(e) => stageSettings({ mailboxPolicy: e.target.value })}>
                        {MAILBOX_POLICIES.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{MAILBOX_POLICIES.find((o) => o.value === shown('mailboxPolicy'))?.label ?? shown('mailboxPolicy')}</span>
                    )}
                    <small className="muted">{MAILBOX_POLICIES.find((o) => o.value === shown('mailboxPolicy'))?.description ?? 'What the mailbox does with messages as they get old.'}</small>
                  </div>
                )}
              </div>
            </Card>

            {/* What callers hear before the beep: admins type it for them, or switch back (1.12.33). */}
            <VoicemailGreetingCard domain={d} extension={extension} own={false} />

            <Card title="Stitch sign-in">
              <div className="card-body section-stack">
                {member ? (
                  <>
                    <span>
                      {member.role === 'admin' ? 'Signs in as an admin' : member.messagingOnly ? 'Signs in for Messages only, with no extension linked' : 'Signs in to see their own phone and calls'}
                      <span className="muted"> · {member.lastSignInAt ? `last signed in ${dateOnly(member.lastSignInAt)}` : 'not signed in yet'}</span>
                    </span>
                    {/* Messaging only, with this person's email: give them this phone (a Stitch change; nothing on the phone system). */}
                    {member.messagingOnly && (
                      <Button size="small" variant="secondary" onClick={() => void linkThem(member.id)}>
                        Link extension {extension} to them
                      </Button>
                    )}
                  </>
                ) : invite ? (
                  <span>
                    Invited <span className="muted">· the link expires {dateOnly(invite.expiresAt)}</span>
                  </span>
                ) : (
                  <>
                    <span className="muted">Doesn’t sign in to Stitch. Invite them to see their own desk phone keys, queues and calls.</span>
                    {writable && (
                      <Button size="small" variant="secondary" disabled={!row?.email} onClick={() => void inviteThem()} title={row?.email ? undefined : 'Add their email first'}>
                        Invite them
                      </Button>
                    )}
                  </>
                )}
                {invited && <ShareLink result={invited} />}
                {inviteError && <Notice tone="crit">{inviteError}</Notice>}
              </div>
            </Card>

            {/* The receptionist seat (1.13.0): only someone who signs in to Stitch with this extension. */}
            {member && member.extension === extension && (
              <SeatCard
                domain={d}
                extension={extension}
                name={name}
                writable={writable}
                onChanged={() => {
                  setVersion((v) => v + 1);
                  people.reload();
                }}
              />
            )}

            {/* Shared phones (1.13.0): only an extension nobody signs in to Stitch with. */}
            {(!member || member.extension !== extension) && people.data?.sharedPhones && (
              <SharedPhoneCard
                domain={d}
                extension={extension}
                name={row?.firstName || name}
                place={place}
                writable={writable}
                featureSet={fields.includes('featureSet') ? (row?.featureSet ?? '') : undefined}
                pendingFeatureSet={pending.featureSet}
                canSetType={canWrite('featureSet')}
                onType={(featureSet) => stageSettings({ featureSet })}
                onChanged={() => people.reload()}
              />
            )}

            <Card title="Where calls ring them">
              <div className="card-body section-stack">
                {routing.data === null ? (
                  <span className="muted">Can’t be read just now.</span>
                ) : refs.length === 0 ? (
                  <span className="muted">Not in any ring group, call queue or menu: only calls to their extension (and their numbers) ring them.</span>
                ) : (
                  <ul className="plain-list">
                    {refs.map((r) => (
                      <li key={`${r.extension}-${r.field}`}>
                        <strong>{r.name}</strong> <span className="muted">({r.where})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>
            {writable && people.data.canRemove && (
              <Link className="link-btn danger-link" to={`${base}/people?remove=${encodeURIComponent(extension)}`}>
                Remove {name}…
              </Link>
            )}
          </div>
        </div>
      </div>

      {staged.length > 0 && !reviewing && (
        <div className="tray" role="region" aria-label="Changes ready to review">
          <span className="grow">
            <strong>{staged.length === 1 ? '1 change ready to review' : `${staged.length} changes ready to review`}</strong>
            <span>Nothing has changed on the phone system yet.</span>
          </span>
          <Button variant="ghost" onClick={() => setStaged([])}>
            Clear
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setReviewKey((k) => k + 1);
              setReviewing(true);
            }}
          >
            Review changes
          </Button>
        </div>
      )}
      <ReviewDrawer
        key={reviewKey}
        open={reviewing}
        tenant={d}
        items={staged as ReviewItem[]}
        onUnstage={(key) => setStaged((cur) => cur.filter((s) => s.key !== key))}
        onClose={(applied) => {
          setReviewing(false);
          if (applied) {
            setStaged([]);
            setVersion((v) => v + 1);
            people.reload();
          }
        }}
      />

      {dialog === 'number' && numbers.data && (
        <NumberDialog
          name={name}
          extension={extension}
          numbers={realNumbers}
          dir={dir}
          onClose={() => setDialog(null)}
          onPick={(n) => {
            stageNumber(n);
            setDialog(null);
          }}
        />
      )}
      {dialog === 'callerId' && (
        <CallerIdDialog
          name={name}
          value={callerId}
          theirs={[...theirNumbers.map((n) => n.extension), ...stagedNumbers]}
          all={realNumbers.map((n) => n.extension)}
          onClose={() => setDialog(null)}
          onSave={(v) => {
            stageSettings({ callerId: v });
            setDialog(null);
          }}
        />
      )}
      {dialog === 'e911' && (
        <EmergencyDialog
          name={name}
          value={shown('emergencyLocation')}
          addresses={savedAddresses}
          onClose={() => setDialog(null)}
          onSave={(v) => {
            stageSettings({ emergencyLocation: v });
            setDialog(null);
          }}
        />
      )}
      {dialog === 'calls' && (
        <CallsDialog
          name={name}
          values={Object.fromEntries(CALL_FIELDS.filter((f) => has(f)).map((f) => [f, shown(f)])) as Partial<Record<CallField, string>>}
          editable={CALL_FIELDS.filter((f) => canWrite(f))}
          dir={dir}
          onClose={() => setDialog(null)}
          onSave={(set) => {
            stageSettings(set);
            setDialog(null);
          }}
        />
      )}
    </>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Locked({ text = 'Ask Stitch to change it' }: { text?: string }) {
  return (
    <span className="muted locked-note">
      <Icon name="lock" size={13} /> {text}
    </span>
  );
}

/** An extension's name, or a phone number written out. */
function nameIn(dir: Directory | undefined, v: string): string {
  const e = dir?.entries.find((x) => x.extension === v);
  if (e) return `${e.name || v} (${v})`;
  return /^\+?\d{7,}$/.test(v) ? formatPhoneNumber(v) : v;
}

function describe(field: string, v: string, dir: Directory | undefined): string {
  if (field === 'dnd') return v === 'true' ? 'on' : 'off';
  if (field === 'noAnswerSeconds') return v ? `${v} seconds` : 'the usual time';
  if (field === 'callerId') return v ? formatPhoneNumber(v) : 'the company’s usual number';
  if (field === 'timeZone') return pbxZoneLabel(v);
  if (field === 'mailboxPolicy') return (MAILBOX_POLICIES.find((o) => o.value === v)?.label ?? v).toLowerCase();
  if (field === 'featureSet') return ALL_FEATURE_SETS.find((o) => o.value === v)?.label ?? v;
  if (field.startsWith('forward')) return v ? nameIn(dir, v) : field === 'forwardNoAnswer' ? 'voicemail' : 'off';
  return v || '(none)';
}

// --- dialogs -------------------------------------------------------------------------------------

function NumberDialog({
  name,
  extension,
  numbers,
  dir,
  onClose,
  onPick
}: {
  name: string;
  extension: string;
  numbers: Account[];
  dir: Directory | undefined;
  onClose: () => void;
  onPick: (n: Account) => void;
}) {
  const free = numbers.filter((n) => !n.values.destination);
  const elsewhere = numbers.filter((n) => n.values.destination && n.values.destination !== extension);
  const [pick, setPick] = useState(free[0]?.extension ?? '');
  const chosen = numbers.find((n) => n.extension === pick);
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Give ${name} a number`}
      subtitle="Calls to it ring them directly."
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!chosen} onClick={() => chosen && onPick(chosen)}>
            Add to changes
          </Button>
        </>
      }
    >
      {numbers.length === 0 ? (
        <Empty title="No phone numbers yet">Get one on Numbers → Get numbers, then give it to them here.</Empty>
      ) : (
        <fieldset className="plain-fieldset">
          <legend className="sr-only">Number</legend>
          {free.length > 0 && <span className="field-label">Not going anywhere yet</span>}
          {free.map((n) => (
            <label key={n.extension} className="radio-card">
              <input type="radio" name="number" checked={pick === n.extension} onChange={() => setPick(n.extension)} />
              <span className="cell-stack">
                <strong>{formatPhoneNumber(n.extension)}</strong>
                <small>{n.live?.number?.label ?? 'Free to use'}</small>
              </span>
            </label>
          ))}
          {elsewhere.length > 0 && <span className="field-label">Going somewhere else now (it moves to them)</span>}
          {elsewhere.map((n) => (
            <label key={n.extension} className="radio-card">
              <input type="radio" name="number" checked={pick === n.extension} onChange={() => setPick(n.extension)} />
              <span className="cell-stack">
                <strong>{formatPhoneNumber(n.extension)}</strong>
                <small>Now: {nameIn(dir, n.values.destination ?? '')}</small>
              </span>
            </label>
          ))}
          <span className="hint">Need a new number? Get one on Numbers → Get numbers.</span>
        </fieldset>
      )}
    </Dialog>
  );
}

function CallerIdDialog({ name, value, theirs, all, onClose, onSave }: { name: string; value: string; theirs: string[]; all: string[]; onClose: () => void; onSave: (v: string) => void }) {
  const [pick, setPick] = useState(value);
  const others = all.filter((n) => !theirs.includes(n));
  const option = (v: string, title: string, sub: string) => (
    <label key={v || 'default'} className="radio-card">
      <input type="radio" name="caller-id" checked={pick === v} onChange={() => setPick(v)} />
      <span className="cell-stack">
        <strong>{title}</strong>
        <small>{sub}</small>
      </span>
    </label>
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title="Caller ID"
      subtitle={`The number people see when ${name} calls out.`}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={pick === value} onClick={() => onSave(pick)}>
            Add to changes
          </Button>
        </>
      }
    >
      <fieldset className="plain-fieldset">
        <legend className="sr-only">Caller ID</legend>
        {option('', 'The company’s usual number', 'Callers see your main number, as set on the phone system.')}
        {theirs.map((n) => option(n, formatPhoneNumber(n), 'Their own number: people can call them straight back.'))}
        {others.map((n) => option(n, formatPhoneNumber(n), 'Another of your numbers'))}
        {value && !all.includes(value) && option(value, formatPhoneNumber(value), 'Set on the phone system')}
      </fieldset>
    </Dialog>
  );
}

/** Where 911 sends help for this person: one of the company's saved addresses. */
function EmergencyDialog({ name, value, addresses, onClose, onSave }: { name: string; value: string; addresses: EmergencyLocation[]; onClose: () => void; onSave: (id: string) => void }) {
  const [pick, setPick] = useState(value);
  return (
    <Dialog
      open
      onClose={onClose}
      title="Emergency address (911)"
      subtitle={`Where emergency services are sent when ${name} calls 911 from their phone. Choose where they actually work.`}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!pick || pick === value} onClick={() => onSave(pick)}>
            Add to changes
          </Button>
        </>
      }
    >
      <fieldset className="plain-fieldset">
        <legend className="sr-only">Emergency address</legend>
        {addresses.map((l) => (
          <label key={l.id} className="radio-card">
            <input type="radio" name="e911" checked={pick === l.id} onChange={() => setPick(l.id)} />
            <span className="cell-stack">
              <strong>{l.name || 'Saved address'}</strong>
              <small>
                {l.address}
                {l.callback ? ` · 911 call-back number ${formatPhoneNumber(l.callback)}` : ''}
              </small>
            </span>
          </label>
        ))}
      </fieldset>
      <p className="muted small">Moving to another address? Ask Stitch support to add it, then choose it here.</p>
    </Dialog>
  );
}

function CallsDialog({
  name,
  values,
  editable,
  dir,
  onClose,
  onSave
}: {
  name: string;
  values: Partial<Record<CallField, string>>;
  editable: CallField[];
  dir: Directory | undefined;
  onClose: () => void;
  onSave: (set: FieldValues) => void;
}) {
  const [v, setV] = useState<Partial<Record<CallField, string>>>(values);
  const [error, setError] = useState('');
  const can = (f: CallField) => editable.includes(f) && values[f] !== undefined;
  const set = (f: CallField) => (x: string) => setV((cur) => ({ ...cur, [f]: x }));
  const save = () => {
    const secs = v.noAnswerSeconds ?? '';
    if (secs && (!/^\d{1,3}$/.test(secs) || Number(secs) < 5 || Number(secs) > 120)) return setError('Ring time: between 5 and 120 seconds, or empty for the usual time.');
    onSave(Object.fromEntries(editable.filter((f) => v[f] !== undefined).map((f) => [f, (v[f] ?? '').trim()])));
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`When calls come in for ${name}`}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save}>
            Add to changes
          </Button>
        </>
      }
    >
      <div className="section-stack">
        {can('dnd') && (
          <label className="check">
            <input type="checkbox" checked={v.dnd === 'true'} onChange={(e) => set('dnd')(e.target.checked ? 'true' : 'false')} />
            Do not disturb: calls go straight to voicemail
          </label>
        )}
        {can('noAnswerSeconds') && (
          <Field label="Ring them for (seconds)" inputMode="numeric" value={v.noAnswerSeconds ?? ''} onChange={(e) => set('noAnswerSeconds')(e.target.value)} hint="Empty: the phone system’s usual time." />
        )}
        {can('forwardNoAnswer') && (
          <DestinationPicker label="When they don’t answer, send calls to" value={v.forwardNoAnswer ?? ''} onChange={set('forwardNoAnswer')} dir={dir} help="Empty: their voicemail. Someone in the company, or any phone number." />
        )}
        {can('forwardBusy') && <DestinationPicker label="When they’re on a call, send calls to" value={v.forwardBusy ?? ''} onChange={set('forwardBusy')} dir={dir} help="Empty: call waiting, then voicemail." />}
        {can('forwardAll') && <DestinationPicker label="Forward all calls to" value={v.forwardAll ?? ''} onChange={set('forwardAll')} dir={dir} help="Every call goes here instead. Empty: off." />}
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Dialog>
  );
}
